import { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, ChevronDown, Target, Users, Brain, Lightbulb, Map, DollarSign, Server, FileText, HelpCircle, Shield, Zap, Bot, GitBranch, Search, Cpu, Megaphone, BrainCircuit } from 'lucide-react';

const SECTIONS = [
  {
    id: 'value-proposition',
    title: 'Value Proposition',
    icon: Target,
    color: '#06B6D4',
    content: `## NEXUS ITSM — AI-Native IT Service Management

### The Problem
Enterprise IT teams spend 60-80% of their time on repetitive incident triage, manual change reviews, and reactive firefighting. Mean Time to Resolution (MTTR) for P1 incidents averages 4+ hours, costing Fortune 500 companies $5,600 per minute of downtime.

### Our Solution
NEXUS ITSM deploys 11 autonomous AI agents that resolve incidents, assess change risk, fulfill service requests, detect security threats, and manage compliance — with zero human intervention for 80%+ of events.

### Key Differentiators

**1. Autonomous Resolution, Not Just Alerting**
Unlike ServiceNow, PagerDuty, or Jira Service Management, NEXUS doesn't just route tickets — it resolves them. ARIA's 9-node state machine executes real remediation (ECS scaling, service restarts, health validation) against live infrastructure.

**2. 11 Specialized AI Agents, Not One Chatbot**
Each agent is purpose-built for its domain: ARIA for incidents, CHANGE-IQ for risk assessment, SENTINL for security, NOVA for pattern detection. They communicate via a message bus and hand off work intelligently.

**3. Real Infrastructure Integration**
NEXUS connects to your actual AWS/Azure/GCP infrastructure via encrypted credentials. When ARIA detects a CPU spike, it queries real CloudWatch metrics, executes real ECS scaling, and validates real ALB health — not mock data.

**4. Compliance Built-In, Not Bolted On**
Every agent action is written to a SHA-256 hash-chained immutable audit trail. Generate SOC2, ISO 20000, SOX, HIPAA, and GDPR compliance reports on demand with real evidence counts.

### Measurable Outcomes
- **MTTR Reduction**: 4 hours → 11 minutes (P1 incidents)
- **Auto-Resolution Rate**: 80-100% of incidents resolved without human intervention
- **Cost Avoidance**: $4,200 saved per auto-resolved incident ($214K+ for 51 incidents)
- **Alert Noise Reduction**: 95% suppression of duplicate alerts
- **FTE Savings**: 0.8 FTE equivalent per month from automated resolution
- **SLA Compliance**: Real-time prediction of breaches 4+ hours in advance`,
  },
  {
    id: 'target-customers',
    title: 'Target Customers',
    icon: Users,
    color: '#10B981',
    content: `## Target Customer Segments

### Primary: Enterprise IT Operations (500+ employees)
- **VP/Director of IT Operations** — Needs to reduce MTTR, improve SLA compliance
- **IT Service Desk Manager** — Drowning in L1 tickets, needs automation
- **CISO / Security Operations** — Needs real-time threat detection with compliance audit
- **CTO / VP Engineering** — Needs to reduce toil and incident fatigue on engineering teams

### Secondary: Managed Service Providers (MSPs)
- Multi-tenant architecture allows MSPs to onboard customers with isolated environments
- Each customer gets their own agents, integrations, and compliance reports
- Per-tenant LLM model selection and cost tracking

### Ideal Customer Profile (ICP)
| Attribute | Criteria |
|-----------|----------|
| Company Size | 500-50,000 employees |
| IT Team | 10-500 IT staff |
| Cloud | AWS, Azure, or GCP primary |
| Monitoring | Datadog, CloudWatch, PagerDuty, Prometheus |
| Compliance | SOC2, ISO 27001, HIPAA, or SOX requirements |
| Monthly Ticket Volume | 500-50,000 incidents/month |
| Current MTTR (P1) | 2+ hours |
| Pain | Manual triage, alert fatigue, compliance overhead |

### Industries
- **Financial Services** — SOX compliance, low-latency requirements
- **Healthcare** — HIPAA audit trail, patient data security
- **SaaS/Technology** — High deployment velocity, microservices complexity
- **E-commerce/Retail** — Revenue-critical uptime, seasonal spikes
- **Government** — FedRAMP requirements, audit trail`,
  },
  {
    id: 'ai-ml-integrations',
    title: 'AI/ML Integrations',
    icon: Brain,
    color: '#6366F1',
    content: `## AI/ML Architecture

### LLM Integration (Claude/GPT/Gemini)
NEXUS uses large language models for reasoning across all 11 agents:

| Agent | LLM Task | Default Model | Why |
|-------|----------|--------------|-----|
| **MOA** | Event classification, priority assignment | Claude 3.5 Haiku | Fast classification (<1s) |
| **ARIA** | Root cause analysis, remediation planning | Claude 4 Sonnet | Complex multi-step reasoning |
| **CHANGE-IQ** | 6-factor risk scoring | Claude 4 Sonnet | Nuanced risk assessment |
| **CONCIERGE** | Intent classification, entity extraction | Claude 3.5 Haiku | Conversational speed |
| **NOVA** | Problem statement generation, KEDB creation | Claude 4 Sonnet | Pattern analysis |
| **SENTINL** | Threat severity assessment, MITRE mapping | Claude 4 Sonnet | Security criticality |
| **CAPACITOR** | Capacity recommendations | Claude 3.5 Haiku | Metric analysis |
| **HERALD** | Notification content generation | Claude 3.5 Haiku | Communication speed |

### Per-Agent Model Selection
Each agent's LLM model is configurable per-tenant:
- **5 models available**: Claude 4 Sonnet, Claude 3.5 Haiku, GPT-4o, GPT-4o Mini, Gemini 2.5 Flash
- **Cost optimization**: Use Haiku ($0.25/MTok) for fast tasks, Sonnet ($3/MTok) for complex reasoning
- **Token budget tracking**: Per-tenant spend monitoring with configurable limits

### Machine Learning Components (Non-LLM)

**NOVA Pattern Detection (TF-IDF + Cosine Similarity)**
- Tokenizes incident text (title + root_cause + description)
- Computes TF-IDF vectors with IDF weighting
- Cosine similarity clustering (threshold: 0.35)
- Trend detection via time-gap analysis

**CAPACITOR Anomaly Detection**
- Threshold breach detection
- Trend extrapolation (linear projection to breach)
- Sudden spike detection (>2x average)
- Alert consolidation and noise reduction

**NOVA Auto-Link**
- Matches new incidents to existing problems via TF-IDF cosine similarity (threshold: 0.30)
- Updates problem records with linked incident counts

### Integration Layer
- **emergentintegrations** library for LLM access (single API key across providers)
- **Circuit breaker** pattern: 3 failures → OPEN state → 60s recovery
- **Fallback**: Every Claude call has a keyword-based fallback for when LLM is unavailable`,
  },
  {
    id: 'patentable-methods',
    title: 'Patentable Methods',
    icon: Lightbulb,
    color: '#F59E0B',
    content: `## Potentially Patentable Methods

### 1. Multi-Agent Autonomous IT Resolution System
**Claim**: A system comprising multiple specialized AI agents (MOA, ARIA, CHANGE-IQ, etc.) that autonomously classify, route, investigate, remediate, and validate IT incidents through a coordinated state machine pipeline without human intervention.

**Novel aspects**:
- 9-node ARIA state machine (ingest→enrich→correlate→classify→rca→plan→execute→validate→pir) with real infrastructure execution
- Confidence-threshold-based autonomous vs. escalation routing
- Claude-powered root cause analysis with real monitoring data context

### 2. Hash-Chained Immutable Audit Trail for IT Operations
**Claim**: A method of maintaining an immutable, SHA-256 hash-chained audit trail where each entry links to the previous via cryptographic hash, enabling tamper-evident compliance reporting for SOC2/HIPAA/SOX.

**Novel aspects**:
- Sequence-numbered entries with previous_hash linking
- Compliance tag association per entry (SOC2_CC6.1, HIPAA_164.308, etc.)
- On-demand regulatory report generation with real evidence counts from the chain

### 3. AI-Driven Change Risk Scoring with Multi-Approver Workflow
**Claim**: A method using LLM-based analysis of 6 weighted risk factors to score change requests 0-100 and automatically route through a 4-tier approval chain (AUTO/CAB/SENIOR/BLOCK) with standard change template matching.

**Novel aspects**:
- Template matching bypasses Claude for known change types (SSL rotation, config update)
- CI conflict detection via affected_cis overlap
- Maintenance window enforcement with dynamic risk adjustment
- Auto-rollback on post-change health degradation

### 4. TF-IDF Semantic Clustering for IT Problem Detection
**Claim**: A method of detecting recurring IT problems by applying TF-IDF vectorization and cosine similarity clustering to incident text, with trend detection (WORSENING/STABLE/IMPROVING) and automatic KEDB entry generation.

### 5. Predictive SLA Breach Engine
**Claim**: A system that predicts SLA breaches 4+ hours in advance using real-time deadline tracking, emitting WebSocket alerts and creating preventive tickets when breach probability exceeds configurable thresholds.

### 6. Encrypted Multi-Tenant Credential Vault with Tool Routing
**Claim**: A method where AI agents automatically select between real infrastructure APIs and mock implementations based on the availability of encrypted, per-tenant credentials, enabling seamless fallback without code changes.`,
  },
  {
    id: 'user-journeys',
    title: 'User Journeys',
    icon: Map,
    color: '#EC4899',
    content: `## User Journeys

### Journey 1: IT Ops Manager — "3am P1 Incident"
1. **3:00 AM** — CloudWatch alarm fires: ECS CPU > 80% on production
2. **3:00:02** — SNS delivers to NEXUS webhook
3. **3:00:03** — MOA classifies as P1 INCIDENT, routes to ARIA (95% confidence)
4. **3:00:04** — ARIA starts 9-node resolution: ingests alert, queries real CloudWatch metrics
5. **3:00:08** — ARIA correlates with 3 similar alerts, identifies pattern
6. **3:00:15** — Claude performs root cause analysis: "Connection pool exhaustion in auth-service"
7. **3:00:25** — ARIA scales ECS tasks from 1 to 2 (real AWS API call)
8. **3:00:35** — ARIA validates: ALB health check passes, error rate < 0.5%
9. **3:00:40** — PIR generated, ticket RESOLVED at 93% confidence
10. **3:00:41** — HERALD sends Slack notification: "✅ Resolved: ECS CPU critical"
11. **3:00:42** — LEARN-X generates knowledge article from the resolution
12. **IT Ops Manager wakes up** — sees resolved incident with full timeline. No pages at 3am.

### Journey 2: Change Manager — "Production Database Migration"
1. Creates change ticket: "Upgrade PostgreSQL 14 to 16"
2. CHANGE-IQ scores risk: 85/100 (6 weighted factors, Claude-analyzed)
3. Routes to BLOCKED tier: requires IT_MANAGER + CISO + CTO approval
4. CAB members review risk scorecard on Changes page
5. IT_MANAGER approves → CISO approves → CTO approves (3/3)
6. CHANGE-IQ executes: pre-change baseline → deploy → post-validate
7. Post-validation detects degradation → auto-rollback triggered
8. Ticket shows full timeline: risk factors, approvals, execution, rollback

### Journey 3: Employee — "I Need VPN Access"
1. Goes to Requests page → types "I need VPN access for a contractor"
2. CONCIERGE classifies intent: VPN_ACCESS (4h SLA, requires IT_MANAGER approval)
3. Creates REQ-4037 with service catalog metadata
4. IT Manager receives approval request
5. Ticket fulfilled → employee notified

### Journey 4: CISO — "Security Threat Detected"
1. SENTINL scheduled scan detects open Security Group (port 22 to 0.0.0.0/0)
2. Claude assesses severity: 85/100 — "Critical Infrastructure Exposure"
3. SENTINL executes containment: audit SGs, collect evidence (CloudTrail + metrics)
4. HERALD notifies security team via Slack + email
5. Report generated with MITRE ATT&CK mapping (T1190)
6. Compliance audit trail tagged: SOC2_CC7.2, HIPAA_164.308

### Journey 5: Compliance Officer — "SOC 2 Audit"
1. Goes to Settings → Compliance & Audit
2. Clicks "SOC 2 Type II" → report generates in seconds
3. Report shows: 292 audit entries, 145 tickets, 27,799 agent actions
4. Hash chain verified: ✅ VERIFIED (292 entries, 0 errors)
5. Sections: CC6.1 (access controls), CC6.6 (change management), CC7.2 (monitoring)
6. Each section shows evidence count from immutable audit trail`,
  },
  {
    id: 'pricing-strategy',
    title: 'Pricing Strategy',
    icon: DollarSign,
    color: '#10B981',
    content: `## Pricing Strategy

### Tier Structure

| Tier | Monthly Price | Agents | Tickets/Month | LLM Budget | Support |
|------|-------------|--------|---------------|-----------|---------|
| **Starter** | $2,999/mo | 4 (MOA, ARIA, HERALD, CONCIERGE) | 1,000 | $50 Claude | Email |
| **Professional** | $7,999/mo | 8 (+ CHANGE-IQ, NOVA, ATLAS, SENTINL) | 10,000 | $200 Claude | Priority |
| **Enterprise** | $19,999/mo | All 11 + COMPLIANCE-OPS | Unlimited | $500 Claude | Dedicated CSM |
| **Platform** | Custom | All + white-label | Unlimited | Custom | Solutions Architect |

### Value-Based Pricing Justification
- Average cost of a P1 incident: **$5,600/minute** (Gartner)
- Average MTTR without NEXUS: **4 hours** = **$1.3M/incident**
- Average MTTR with NEXUS: **11 minutes** = **$61,600/incident**
- **Savings per P1: $1.24M**
- At 2 P1s/month: **$2.48M saved** → 10x ROI on Enterprise tier

### Add-Ons
| Add-On | Price | What |
|--------|-------|------|
| Additional LLM budget | $10/100K tokens | Top up Claude/GPT budget |
| Custom agent development | $25,000 one-time | Purpose-built agent for your domain |
| SOC 2 audit preparation | $15,000 one-time | COMPLIANCE-OPS + consultant review |
| Multi-region deployment | $5,000/mo | Deploy in US + EU + APAC |
| Dedicated infrastructure | $10,000/mo | Single-tenant deployment |

### Free Trial
- 14-day trial with Professional tier
- 7 demo scenarios included
- Up to 100 real incidents
- Full access to all 11 agents`,
  },
  {
    id: 'technical-architecture',
    title: 'Technical Architecture',
    icon: Server,
    color: '#3B82F6',
    content: `## Technical Architecture

### Stack
| Layer | Technology |
|-------|-----------|
| **Frontend** | React 18, Tailwind CSS, shadcn/ui, Recharts, Socket.IO client |
| **Backend** | FastAPI (Python), python-socketio, Pydantic |
| **Database** | MongoDB (28 indexed collections) |
| **LLM** | Claude 4 Sonnet / Haiku via emergentintegrations |
| **Real-time** | Socket.IO with tenant + ticket room subscriptions |
| **Auth** | JWT (HS256), bcrypt password hashing, RBAC (28 permissions, 8 roles) |
| **Encryption** | Fernet AES-256 for credential vault |
| **Infrastructure** | Kubernetes-ready with health probes (/health/ready, /health/live) |

### Agent Architecture
\`\`\`
CloudWatch/Datadog/PagerDuty Alert
  → Webhook Endpoint (signature validation)
    → MOA (classify + route)
      → ARIA (9-node state machine) ←→ AWS APIs (real execution)
      → CHANGE-IQ (11-node + approval chain) ←→ AWS APIs
      → SENTINL (threat detection) ←→ CloudTrail/SGs
      → CONCIERGE (intent → fulfill)
      → NOVA (pattern detection) → LEARN-X (KB generation)
      → ATLAS (discovery) ←→ EC2/RDS/ECS/ELB APIs
      → CAPACITOR (metrics) ←→ CloudWatch APIs
    → HERALD (notify) → Slack/Teams/Email
    → COMPLIANCE-OPS (audit trail)
\`\`\`

### Background Schedulers (8 active)
| Scheduler | Interval | Task |
|-----------|----------|------|
| Mock events | 3-5s | Agent activity simulation |
| SLA breach | 60s | Breach risk WebSocket alerts |
| NOVA | 30 min | TF-IDF pattern detection |
| ATLAS | 15 min drift / 60 min discovery | Real AWS infrastructure scan |
| LEARN-X | 60 min | Auto-generate KB articles |
| CAPACITOR | 15 min | Real CloudWatch metric analysis |
| SENTINL | 30 min | Real AWS threat scan |
| COMPLIANCE-OPS | 60 min | Hash chain verification |

### Database Collections (18)
tickets, users, tenants, cis, ci_relationships, agent_actions, agent_configs, agent_state, agent_messages, agent_queue, scaling_events, knowledge_articles, audit_entries, immutable_audit, credentials, compliance_reports, concierge_chats, onboarding, satisfaction_surveys, status_updates, password_resets, analytics_queries

### Security
- Encrypted credential vault (Fernet AES-256)
- Brute force protection (5 attempts → 15 min lockout)
- Circuit breakers for external APIs
- Rate limiting (200 req/min per IP)
- Input sanitization (HTML tag stripping, script injection prevention)
- RBAC with 28 atomic permissions across 8 roles`,
  },
  {
    id: 'product-guide',
    title: 'Product Guide',
    icon: FileText,
    color: '#F97316',
    content: `## Product Guide

### Pages & Features

| Page | Route | What It Does |
|------|-------|-------------|
| **Command Center** | \`/\` | Real-time dashboard: 8 KPIs, 11 agents, heat map, MTTR trend, SLA burn, live feed |
| **Incidents** | \`/incidents\` | Filterable table, live SLA countdown, bulk actions, priority toggles |
| **Incident Detail** | \`/incidents/:id\` | ARIA live feed, confidence gauge, timeline, human override, approve/reject |
| **Changes** | \`/changes\` | CAB approval queue, change calendar, risk scorecard, multi-approver chain |
| **Requests** | \`/requests\` | CONCIERGE conversational AI, service catalog (9 types), satisfaction survey |
| **Problems** | \`/problems\` | NOVA pattern detection, KEDB entries, trend indicators, linked incidents |
| **Security** | \`/security\` | SENTINL threat list, severity scores, containment status, evidence |
| **Assets** | \`/assets\` | CMDB grid, search/filter, CI detail + relationships + impact analysis |
| **Topology** | \`/topology\` | Interactive CI relationship graph with dependency filtering |
| **Agents** | \`/agents\` | Agent Studio (11 agents), activity stream, scaling & orchestration |
| **Knowledge** | \`/knowledge\` | Category tabs, markdown articles, KEDB integration, effectiveness stats |
| **Analytics** | \`/analytics\` | 6 KPIs, resolution trend, MTTR, agent leaderboard, cost avoidance |
| **NLP Query** | \`/analytics/query\` | Natural language → analytics with chart rendering |
| **Settings** | \`/settings\` | 25 scenarios, team management, SLA config, integrations, compliance |

### 25 Triggerable Scenarios
Accessible from Settings → Agent Scenarios. Each triggers a full agent workflow:
- 7 ARIA incident scenarios (Memory Leak, DB Pool, Disk, Cert, Latency, Alert Storm, Ambiguous)
- 2 CHANGE-IQ scenarios (Low Risk, High Risk)
- 1 NOVA pattern detection
- 1 ATLAS discovery scan
- 3 CAPACITOR scenarios (Analysis, Prediction, Noise Suppression)
- 2 HERALD scenarios (P1 Blast, Daily Digest)
- 3 SENTINL scenarios (Threat, Malware, Guardrail)
- 2 COMPLIANCE-OPS scenarios (SOC 2 Report, Chain Verify)
- 1 LEARN-X KB generation
- 3 E2E scenarios (Real Alert, SLA Warning, P1 Pipeline)

### Integration Points
| Integration | Webhook URL | What It Does |
|------------|-------------|-------------|
| Datadog | \`/api/webhooks/datadog\` | Receives alerts → MOA → ARIA |
| PagerDuty | \`/api/webhooks/pagerduty\` | Receives incidents → MOA |
| CloudWatch | \`/api/webhooks/cloudwatch\` | SNS subscription + alarm routing |
| Prometheus | \`/api/webhooks/prometheus\` | Alertmanager integration |
| Generic | \`/api/webhooks/generic\` | Any monitoring tool |`,
  },
  {
    id: 'user-guide',
    title: 'User Guide',
    icon: HelpCircle,
    color: '#8B5CF6',
    content: `## User Guide

### Getting Started (5 Minutes)

**Step 1: Login**
Navigate to your NEXUS URL and login with your credentials.
- Default admin: \`admin@nexus.corp\` / \`admin123\`

**Step 2: Watch the Command Center**
The dashboard shows live operations: agent activity, KPIs, SLA burn rate, incident heat map.

**Step 3: Trigger a Demo**
Go to Settings → Agent Scenarios → click "Memory Leak (P1)". Watch ARIA resolve it autonomously in the Incidents page.

**Step 4: Connect Your Infrastructure**
Go to Settings → Integrations → Configure AWS/Datadog/Slack with your credentials.

### Role-Based Access

| Role | What They Can Do |
|------|-----------------|
| **ORG_ADMIN** | Everything — manage users, configure agents, view compliance |
| **IT_MANAGER** | Manage incidents, approve changes, configure agents |
| **CHANGE_MANAGER** | Approve changes, view incidents and assets |
| **CISO** | Approve security actions, view compliance reports |
| **SERVICE_DESK** | Create/edit incidents, view knowledge base |
| **END_USER** | View knowledge base only |
| **READ_ONLY** | View all pages, no actions |

### Common Tasks

**Create an Incident**
Command Center → Quick Actions → Create Incident → fill title, priority → Submit.
MOA will classify and route it. ARIA will start resolution automatically.

**Approve a Change**
Changes page → CAB Approval Queue → click "Review" → examine risk factors → "Approve as [role]".
Once all required approvals are collected, CHANGE-IQ executes automatically.

**Chat with CONCIERGE**
Requests page → type your request (e.g., "I need a password reset") → CONCIERGE creates a ticket with the right SLA.

**Generate a Compliance Report**
Settings → Compliance & Audit → click SOC2/ISO/HIPAA → report generates with real evidence from audit trail.

**View CI Dependencies**
Assets → click any asset → see relationships (DEPENDS_ON, CONNECTED_TO) → click "Analyze Impact Radius" for blast radius.

**Check Agent Health**
Agents → see all 11 agents with real-time status → click "Scaling & Orchestration" for worker pools and model configuration.

### Keyboard Shortcuts
| Shortcut | Action |
|----------|--------|
| ⌘K | Open global search |
| Click ticket ID | Navigate to detail |
| ESC | Close modals |

### Troubleshooting
- **"OFFLINE" in header**: WebSocket disconnected. Check internet connection. Auto-reconnects.
- **Agent stuck in PROCESSING**: Check Agent Health (/agents/health). May need scenario re-trigger.
- **SLA countdown red**: Ticket approaching breach. Check Incidents page and prioritize.
- **Integration "Not configured"**: Go to Settings → Integrations and add your API keys.`,
  },
];

export default function FullDocsPage() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = searchTerm
    ? SECTIONS.filter(s => s.title.toLowerCase().includes(searchTerm.toLowerCase()) || s.content.toLowerCase().includes(searchTerm.toLowerCase()))
    : SECTIONS;

  return (
    <div data-testid="full-docs-page" className="w-full max-w-[1200px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">NEXUS ITSM Documentation</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Complete platform documentation — {SECTIONS.length} sections</p>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] mb-4">
        <BookOpen size={16} className="text-[var(--nexus-text-muted)]" />
        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search documentation..." className="bg-transparent text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none flex-1" />
      </div>

      {/* Table of Contents */}
      {!expandedSection && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {filtered.map(section => {
            const Icon = section.icon;
            return (
              <button key={section.id} onClick={() => setExpandedSection(section.id)}
                data-testid={`doc-${section.id}`}
                className="nexus-card relative overflow-hidden p-4 text-left hover:bg-white/[0.02] transition-colors group">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${section.color}15` }}>
                    <Icon size={18} style={{ color: section.color }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-accent)] transition-colors">{section.title}</h3>
                    <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">{section.content.split('\n').find(l => l.startsWith('###'))?.replace('### ', '') || 'Click to read'}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Expanded Section */}
      {expandedSection && (() => {
        const section = SECTIONS.find(s => s.id === expandedSection);
        if (!section) return null;
        const Icon = section.icon;
        return (
          <div className="animate-nexus-slide-in">
            <button onClick={() => setExpandedSection(null)} className="text-xs text-[var(--nexus-accent)] hover:underline mb-3">&larr; Back to all sections</button>
            <div className="nexus-card relative overflow-hidden">
              <div className="p-4 border-b border-[var(--nexus-border)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${section.color}15` }}>
                    <Icon size={18} style={{ color: section.color }} />
                  </div>
                  <h2 className="text-lg font-semibold text-[var(--nexus-text-primary)]">{section.title}</h2>
                </div>
              </div>
              <ScrollArea className="h-[600px]">
                <div className="p-6 prose prose-invert prose-sm max-w-none text-[var(--nexus-text-secondary)] leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[var(--nexus-text-primary)] [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-[var(--nexus-text-primary)] [&_h3]:mt-3 [&_h3]:mb-1 [&_strong]:text-[var(--nexus-text-primary)] [&_code]:text-[var(--nexus-accent)] [&_code]:bg-[var(--nexus-bg-elevated)] [&_code]:px-1 [&_code]:rounded [&_li]:ml-4 [&_table]:w-full [&_th]:text-left [&_th]:text-[10px] [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-[var(--nexus-text-muted)] [&_th]:pb-2 [&_td]:text-xs [&_td]:py-1 [&_td]:text-[var(--nexus-text-secondary)] [&_tr]:border-b [&_tr]:border-[var(--nexus-border)]">
                  {section.content.split('\n').map((line, i) => {
                    if (line.startsWith('## ')) return <h2 key={i}>{line.replace('## ', '')}</h2>;
                    if (line.startsWith('### ')) return <h3 key={i}>{line.replace('### ', '')}</h3>;
                    if (line.startsWith('**') && line.endsWith('**')) return <p key={i}><strong>{line.replace(/\*\*/g, '')}</strong></p>;
                    if (line.startsWith('- ')) return <li key={i}>{line.replace('- ', '')}</li>;
                    if (line.startsWith('| ') && line.includes('|')) {
                      const cells = line.split('|').filter(c => c.trim());
                      if (cells.every(c => c.trim().match(/^[-:]+$/))) return null; // separator
                      return <tr key={i}>{cells.map((c, j) => i === 0 || line.includes('---') ? <th key={j}>{c.trim()}</th> : <td key={j}>{c.trim()}</td>)}</tr>;
                    }
                    if (line.startsWith('```')) return null;
                    if (line.trim() === '') return <br key={i} />;
                    return <p key={i} className="text-xs leading-relaxed">{line}</p>;
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
